import { logToNewRelic } from '../services/debug';
import { readGeoCoordsOverride } from '../stores/debug-env/geoCoordsOverride';

import { singleFlight } from './singleFlight';

type GeoLocation = {
  coords: {
    accuracy: number | null;
    altitude: number | null;
    altitudeAccuracy: number | null;
    heading: number | null;
    latitude: number;
    longitude: number;
    speed: number | null;
  };
  timestamp: number;
};

const GEO_OPTIONS: PositionOptions = {
  timeout: 3000,
  maximumAge: 60000,
};

// Set by the location switch in the profile menu: the player opted out of sharing GPS with us, regardless of the
// browser permission.
const LOCATION_OPT_OUT_KEY = 'location_opt_out';

export const isLocationOptedOut = (): boolean => localStorage.getItem(LOCATION_OPT_OUT_KEY) === 'true';

export const setLocationOptOut = (value: boolean): void => {
  localStorage.setItem(LOCATION_OPT_OUT_KEY, String(value));
};

export const isGeolocationAvailable = (): boolean => 'geolocation' in navigator;

export const isGeolocationEnabled = async (): Promise<boolean> => {
  if (!('geolocation' in navigator) || !('permissions' in navigator)) {
    return Promise.resolve(false);
  }
  const permissionStatus = await navigator.permissions.query({ name: 'geolocation' });
  return permissionStatus.state === 'granted';
};

export const getGeolocationPermissionState = async (): Promise<PermissionState | null> => {
  if (!('geolocation' in navigator) || !('permissions' in navigator)) {
    return null;
  }
  const permissionStatus = await navigator.permissions.query({ name: 'geolocation' });
  return permissionStatus?.state || null;
};

export const askForUserLocationPermission = (): Promise<boolean> =>
  new Promise((resolve) => {
    if (!('geolocation' in navigator)) {
      resolve(false);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      () => {
        resolve(true);
      },
      () => {
        resolve(false);
      },
      GEO_OPTIONS,
    );
  });

// Single-flight: auth (login) and LiveGeoStore (current-geo) both ask for coords at boot; share one lookup.
export const getUserLocation = singleFlight(
  (): Promise<GeoLocation | null> =>
    new Promise((resolve) => {
      try {
        // Debug panel override (dev builds only): wins over the opt-out, the permission and the real fix.
        const debugCoords = readGeoCoordsOverride();
        if (debugCoords) {
          resolve({
            coords: {
              accuracy: null,
              altitude: null,
              altitudeAccuracy: null,
              heading: null,
              latitude: debugCoords.latitude,
              longitude: debugCoords.longitude,
              speed: null,
            },
            timestamp: Date.now(),
          });
          return;
        }
        if (isLocationOptedOut() || !('geolocation' in navigator) || !('permissions' in navigator)) {
          resolve(null);
          return;
        }
        void navigator.permissions
          .query({ name: 'geolocation' })
          .then((permissionStatus) => {
            if (permissionStatus.state === 'granted') {
              navigator.geolocation.getCurrentPosition(
                (position) => {
                  resolve(position);
                },
                () => {
                  resolve(null);
                },
                GEO_OPTIONS,
              );
            } else {
              resolve(null);
            }
          })
          .catch(() => {
            resolve(null);
          });
      } catch (error) {
        const err = error as Error;
        void logToNewRelic('Failed to get user location', `Error in getUserLocation`, 'error', {
          error: {
            message: err.message,
          },
        });
        resolve(null);
      }
    }),
);

/** Value for the `x-geo-coords` header: base64-encoded JSON of the GPS position. */
export const encodeGeoCoords = (geoData: GeoLocation | null): string | undefined =>
  geoData ? window.btoa(JSON.stringify(geoData)) : undefined;
